'use client';
import { FC, useState, useEffect } from 'react';
import classNames from 'classnames';
import style from './style.module.scss';
import { useFetchClientSide } from '@/hooks';
import { Null, Preloader } from '..';

interface IPaginationProps {
  link: string;
  setData: any;
  isPagination?: boolean;
  searchValue?: string;
  setSearchValue: (arg: string) => void;
  altSearch?: string;
  loader: any;
  isSearch?: boolean;
  setSearchStatus?: (arg:boolean) => void;
}

export const Pagination: FC<IPaginationProps> = ({
  link,
  setData,
  isPagination = false,
  searchValue = '',
  setSearchValue,
  altSearch,
  loader,
  isSearch = true,
  setSearchStatus,
}) => {
  const [page, setPage] = useState(1);
  const [maxVisibleButtons, setMaxVisibleButtons] = useState(10); // Default to 10 for desktop
  const query = altSearch
    ? `${process.env.NEXT_PUBLIC_API_URL}/${link}${altSearch}`
    : !isSearch
    ? `${process.env.NEXT_PUBLIC_API_URL}/${link}`
    : `${process.env.NEXT_PUBLIC_API_URL}/${link}?pagination[page]=${page}&pagination[pageSize]=5&populate=*&filters[$or][0][title][$contains]=${searchValue}&filters[$or][1][description][$contains]=${searchValue}&sort[createdAt]=desc`;
  const { data, meta, loading: loaderItems, error } = useFetchClientSide(query);

  useEffect(() => {
    setData(data);
    loader(loaderItems);
  }, [data, loader, loaderItems]);

  useEffect(() => {
    if (searchValue.length > 0 && data && data.length === 0) {
      setSearchStatus?.(true)
    } else {
      setSearchStatus?.(false)
    }
  }, [searchValue, data]); // Зависимость от searchValue и data

  useEffect(() => {
    const updateMaxVisibleButtons = () => {
      if (window.innerWidth < 768) {
        setMaxVisibleButtons(4);
      } else {
        setMaxVisibleButtons(10);
      }
    };

    updateMaxVisibleButtons();
    window.addEventListener('resize', updateMaxVisibleButtons);

    return () => {
      window.removeEventListener('resize', updateMaxVisibleButtons);
    };
  }, []);

  const handleTopScroll = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 0);
  };

  const handleIncrementPage = () => {
    if (page >= meta?.pageCount) {
      return;
    }
    setPage(page + 1);
    handleTopScroll();
  };

  const handleDecrementPage = () => {
    if (page === 1) {
      return;
    }
    setPage(page - 1);
    handleTopScroll();
  };

  const handleSetPage = (e: number) => {
    setPage(e);
    handleTopScroll();
  };

  const renderPageButtons = () => {
    const totalPages = meta?.pageCount || 1;
    const start = Math.max(1, page - Math.floor(maxVisibleButtons / 2));
    const end = Math.min(totalPages, start + maxVisibleButtons - 1);

    const buttons = [];
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          className={classNames(style.circleButton, {
            [style.active]: page === i,
          })}
          onClick={() => handleSetPage(i)}>
          {i}
        </button>,
      );
    }

    return buttons;
  };

  return (
    <div className={style.wrapper}>
      <button className={style.button} onClick={handleDecrementPage}>
        Назад
      </button>
      {isPagination && <div className={style.counts}>{renderPageButtons()}</div>}
      <button className={style.button} onClick={handleIncrementPage}>
        Вперед
      </button>
    </div>
  );
};
